/** Compares the `config_after` of a historic `Tour_Operator_Config_Change`
 *  with the live `currentConfig`, and lets the user cherry-pick which keys
 *  to overwrite. The merge is local — it does NOT save to the backend.
 *
 *  Per-key model:
 *    - "added"   — key exists in history but not in current → would create
 *    - "removed" — key exists in current but not in history → would delete
 *    - "changed" — leaf values differ                       → would overwrite
 *    - "same"    — values equal                             → no-op
 *
 *  Default selection: every differing path is pre-checked; "same" paths are
 *  shown but unchecked (per spec). Submit returns the new config the parent
 *  should adopt into its in-memory edit state.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import * as topsApi from '@/api/tourOperators';

type DiffOp = 'added' | 'removed' | 'changed' | 'same';

interface DiffRow {
  /** Dotted path, e.g. "i18n.lang_enabled". */
  path: string;
  op: DiffOp;
  /** Value in the current (live) config. */
  current: unknown;
  /** Value in the historic config_after. `undefined` if absent. */
  historic: unknown;
}

/** Walks two objects in lock-step and emits one DiffRow per leaf path. */
function diffConfigs(
  current: unknown,
  historic: unknown,
  prefix = '',
): DiffRow[] {
  const isObj = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

  if (isObj(current) && isObj(historic)) {
    const keys = new Set([...Object.keys(current), ...Object.keys(historic)]);
    const out: DiffRow[] = [];
    [...keys].sort().forEach((k) => {
      const path = prefix ? `${prefix}.${k}` : k;
      const hasCur = k in current;
      const hasHist = k in historic;
      if (hasCur && hasHist) {
        out.push(...diffConfigs(current[k], historic[k], path));
      } else if (!hasCur && hasHist) {
        out.push({ path, op: 'added', current: undefined, historic: historic[k] });
      } else if (hasCur && !hasHist) {
        out.push({ path, op: 'removed', current: current[k], historic: undefined });
      }
    });
    return out;
  }

  // Leaf comparison — arrays and primitives via JSON equality (matches the
  // backend's whole-array replacement semantics).
  const equal = JSON.stringify(current) === JSON.stringify(historic);
  return [{
    path: prefix || '(root)',
    op: equal ? 'same' : 'changed',
    current,
    historic,
  }];
}

function preview(v: unknown, max = 80): string {
  if (v === undefined) return '—';
  let s: string;
  try {
    s = JSON.stringify(v);
  } catch {
    s = String(v);
  }
  if (s === undefined) return '—';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Apply checked paths from `historic` onto `current`, returning a new config.
 *  Honors all three diff ops:
 *    - "added": set the value at `path` in current to historic's value
 *    - "removed": delete `path` from current
 *    - "changed": overwrite the leaf value at `path` with historic's value
 */
function applyMerge(
  current: Record<string, unknown>,
  diff: DiffRow[],
  checked: Set<string>,
): Record<string, unknown> {
  // Deep-clone via JSON to avoid mutating the source.
  const next = JSON.parse(JSON.stringify(current)) as Record<string, unknown>;

  function setPath(target: Record<string, unknown>, parts: string[], value: unknown) {
    const [head, ...tail] = parts;
    if (!head) return;
    if (tail.length === 0) {
      target[head] = value;
      return;
    }
    const child = target[head];
    if (typeof child !== 'object' || child === null || Array.isArray(child)) {
      target[head] = {};
    }
    setPath(target[head] as Record<string, unknown>, tail, value);
  }

  function deletePath(target: Record<string, unknown>, parts: string[]) {
    const [head, ...tail] = parts;
    if (!head) return;
    if (tail.length === 0) {
      delete target[head];
      return;
    }
    const child = target[head];
    if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
      deletePath(child as Record<string, unknown>, tail);
    }
  }

  for (const row of diff) {
    if (!checked.has(row.path) || row.op === 'same') continue;
    const parts = row.path.split('.');
    if (row.op === 'removed') {
      deletePath(next, parts);
    } else {
      // 'added' and 'changed' both set the value to the historic snapshot.
      setPath(next, parts, row.historic);
    }
  }
  return next;
}

interface Props {
  open: boolean;
  tourOperatorId: number;
  /** id of the Tour_Operator_Config_Change row being merged from. */
  changeId: number | null;
  /** Live in-memory config the user is editing (may have unsaved edits). */
  currentConfig: Record<string, unknown>;
  onMerge: (mergedConfig: Record<string, unknown>) => void;
  onCancel: () => void;
}

export function MergeConfigDialog({
  open,
  tourOperatorId,
  changeId,
  currentConfig,
  onMerge,
  onCancel,
}: Props) {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [showSame, setShowSame] = useState(false);

  // Fetch the historic snapshot when the dialog opens.
  const historyQuery = useQuery({
    queryKey: ['tourOperators', tourOperatorId, 'config_change', changeId],
    queryFn: () =>
      topsApi.retrieveConfigChange(tourOperatorId, changeId as number),
    enabled: open && changeId !== null,
    staleTime: 5 * 60 * 1000,
  });

  // The historic "loaded" config is config_after (state right after that
  // change was applied — what the user is restoring towards).
  const historicConfig = historyQuery.data?.config_after ?? null;

  const diff = useMemo<DiffRow[]>(() => {
    if (!historicConfig) return [];
    return diffConfigs(currentConfig, historicConfig);
  }, [currentConfig, historicConfig]);

  // What THIS revision changed (before → after of the row itself). Distinct
  // from `diff`, which compares the snapshot against the CURRENT config —
  // for the latest change that comparison is empty by definition, which used
  // to read as "history recorded nothing".
  const revisionDiff = useMemo<DiffRow[]>(() => {
    if (!historyQuery.data) return [];
    return diffConfigs(
      (historyQuery.data.config_before as Record<string, unknown> | null) ?? {},
      (historyQuery.data.config_after as Record<string, unknown> | null) ?? {},
    ).filter((d) => d.op !== 'same');
  }, [historyQuery.data]);

  // Initialise the checked set when the diff finishes loading: every
  // differing path is pre-checked, identical paths start unchecked.
  useEffect(() => {
    if (diff.length === 0) {
      setChecked(new Set());
      return;
    }
    setChecked(new Set(diff.filter((d) => d.op !== 'same').map((d) => d.path)));
  }, [diff]);

  function toggle(path: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }

  const visible = useMemo(
    () => (showSame ? diff : diff.filter((d) => d.op !== 'same')),
    [diff, showSame],
  );

  const counts = useMemo(() => {
    const c = { added: 0, removed: 0, changed: 0, same: 0 };
    diff.forEach((d) => c[d.op]++);
    return c;
  }, [diff]);

  function handleMerge() {
    if (!historicConfig) return;
    const merged = applyMerge(currentConfig, diff, checked);
    onMerge(merged);
  }

  function handleClose() {
    setChecked(new Set());
    onCancel();
  }

  const checkedCount = [...checked].filter((p) =>
    diff.some((d) => d.path === p && d.op !== 'same'),
  ).length;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        Merge configuration from change #{changeId ?? '—'}
      </DialogTitle>
      <DialogContent dividers>
        {historyQuery.isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading historic configuration…
            </Typography>
          </Box>
        )}
        {historyQuery.isError && (
          <Alert severity="error">
            Couldn’t load the historic config change.
          </Alert>
        )}

        {historicConfig && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                What this change did ({revisionDiff.length} key{revisionDiff.length === 1 ? '' : 's'})
              </Typography>
              {revisionDiff.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This revision recorded no config difference.
                </Typography>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    maxHeight: 180,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                >
                  {revisionDiff.map((d) => (
                    <Box key={d.path} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>{d.path}</Box>
                      {d.op === 'added' && (
                        <Box component="span" sx={{ color: 'success.main' }}>
                          + {preview(d.historic)}
                        </Box>
                      )}
                      {d.op === 'removed' && (
                        <Box component="span" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
                          {preview(d.current)}
                        </Box>
                      )}
                      {d.op === 'changed' && (
                        <>
                          <Box component="span" sx={{ color: 'error.main' }}>{preview(d.current)}</Box>
                          <Box component="span">→</Box>
                          <Box component="span" sx={{ color: 'success.main' }}>{preview(d.historic)}</Box>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                The grid below compares this revision&apos;s snapshot with the CURRENT
                configuration — checked rows will overwrite the corresponding current
                values (for the newest revision everything matches, so there is
                nothing to merge). The change is not saved automatically.
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip size="small" color="success" label={`+${counts.added} added`} />
              <Chip size="small" color="warning" label={`${counts.changed} changed`} />
              <Chip size="small" color="error" label={`-${counts.removed} removed`} />
              <Chip size="small" variant="outlined" label={`${counts.same} unchanged`} />
              <Box sx={{ flexGrow: 1 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showSame}
                    onChange={(e) => setShowSame(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Show unchanged rows</Typography>}
                sx={{ m: 0 }}
              />
            </Box>

            <Box
              sx={{
                maxHeight: 480,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {visible.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No differing keys — the historic config matches the current one.
                </Typography>
              )}
              {visible.map((row) => (
                <Box
                  key={row.path}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor:
                      row.op === 'added' ? 'success.lighter'
                      : row.op === 'removed' ? 'error.lighter'
                      : row.op === 'changed' ? 'warning.lighter'
                      : 'transparent',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={checked.has(row.path)}
                    onChange={() => toggle(row.path)}
                    disabled={row.op === 'same'}
                    sx={{ p: 0.25, mt: 0.25 }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.25 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}
                      >
                        {row.path}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={row.op}
                        color={
                          row.op === 'added' ? 'success'
                          : row.op === 'removed' ? 'error'
                          : row.op === 'changed' ? 'warning'
                          : 'default'
                        }
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, fontFamily: 'monospace', fontSize: 12 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.disabled">current</Typography>
                        <Box sx={{ wordBreak: 'break-all' }}>{preview(row.current)}</Box>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.disabled">history</Typography>
                        <Box sx={{ wordBreak: 'break-all', color: row.op === 'same' ? 'text.disabled' : 'text.primary' }}>
                          {preview(row.historic)}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleMerge}
          disabled={!historicConfig || checkedCount === 0}
        >
          Merge configuration{checkedCount > 0 ? ` (${checkedCount})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
